/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Collapse, Grid, Row, Skeleton, Spin, Typography, Tooltip } from 'antd';
import { CheckOutlined, LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';
import ActivitiesTable from '../Tables/ActivitiesTable';
import ActivityTypeTag from './ActivityTypeTag';
import MainActivityButton from './MainActivityButton';
import ActivityFlowEditor from './ActivityFlowEditor';
import BotLanguageSelect from '../UI/BotLanguageSelect';

// Misc imports
import { useFetchActivityQuery, useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import { ACTIVITY_TYPES } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

// Constant declarations
const { useBreakpoint } = Grid;
export const PANEL_KEYS = {
  LIST: 'list',
  EDIT: 'edit'
};

const ActivitiesView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(location.search);
  const { isEditingPrimaryLanguage, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm) && !screens.md;

  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId }, false);
  const { data: activity, isFetching: isFetchingActivity } = useFetchActivityQuery({ orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, { skip: !searchParams.get('activity') });
  const [updateActivity, { isLoading: isUpdatingActivity }] = useUpdateActivityMutation({ fixedCacheKey: 'flowGraph-update-activity' });
  const [activePanelKey, setActivePanelKey] = useState(searchParams.has('activity') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);
  const [currentActivityName, setCurrentActivityName] = useState(activity?.name?.[currentBotLanguage] || activity?.name?.[primaryBotLanguage]); // Keep activity name in the state because we need to update it without refetching

  useEffect(() => {
    setActivePanelKey(searchParams.has('activity') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);
  }, [searchParams]);

  useEffect(() => {
    setCurrentActivityName(activity?.name?.[currentBotLanguage] || activity?.name?.[primaryBotLanguage]);
  }, [activity, currentBotLanguage]);

  const onBackToListClick = () => {
    searchParams.delete('activity');

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });

    setActivePanelKey(PANEL_KEYS.LIST);
  };

  // Handle the update of the activity display name
  const onActivityNameSubmit = async (updatedName) => {
    if (updatedName && updatedName !== currentActivityName) {
      const prevValue = updatedName;

      setCurrentActivityName(updatedName);

      const { data: isSaveSuccessful } = await updateActivity({ orgId, tenantId, activityId: activity.id, data: { name: { ...activity.name, [currentBotLanguage]: updatedName } }, doNotInvalidate: false });

      // If save was not successful, fall back to the previous activity name
      if (!isSaveSuccessful) {
        setCurrentActivityName(prevValue);
      }
    }
  };

  const renderActivityName = () => {
    if (isFetchingActivity) {
      return (
        <Skeleton.Input active className="d-block opacity-70 border-radius-base" />
      );
    }

    if (activity) {
      return (
        <div className="d-flex flex-nowrap w-100 align-items-center">
          <div className="flex-fill" style={{ minWidth: 0 }}>
            <Typography.Title
              editable={{
                triggerType: currentActivityName ? 'text' : 'icon',
                enterIcon: <CheckOutlined />,
                onChange: onActivityNameSubmit,
                autoSize: { maxRows: 1, minRows: 1 }
              }}
              level={5}
              className="activity-title d-inline-block v-align-middle my-0 mr-2"
            >
              {currentActivityName}
            </Typography.Title>
            {
              isUpdatingActivity ? (
                <Spin indicator={<LoadingOutlined className="text-secondary" spin />} />
              ) : null
            }
            <ActivityTypeTag type={activity.type} className="mx-1 mr-0" />
            <MainActivityButton
              chaptersource={activity}
              buttonType="text"
              botId={tenantId}
              className="border-0 v-align-middle"
              disabled={activity.type === ACTIVITY_TYPES.USER_FEEDBACK || !isEditingPrimaryLanguage}
            />
            <Tooltip title={t('Drag and drop new nodes and connect them to create flows. To remove an element, select it and press Backspace (on Windows & Linux) or Delete (on Mac).')}>
              <InfoCircleOutlined className="ml-2 fs-md text-secondary" />
            </Tooltip>
          </div>

          {
            !isMobileOrTabletSize && (
              <div className="ml-auto flex-shrink-0">
                <BotLanguageSelect
                  className="d-block"
                  size="large"
                  selectStyle={{ width: 170 }}
                />
              </div>
            )
          }

        </div>
      );
    }

    // Else
    return null;
  };

  return (
    <Collapse
      accordion
      expandIcon={() => null}
      ghost
      activeKey={activePanelKey}
      className="ant-collapse-p-0"
    >
      <Collapse.Panel key={PANEL_KEYS.LIST}>
        <ActivitiesTable />
      </Collapse.Panel>

      <Collapse.Panel key={PANEL_KEYS.EDIT}>
        <Row className="mb-1" align="middle" wrap={false}>
          <Col>
            <Button
              icon={<ArrowLeftIcon />}
              onClick={onBackToListClick}
              type="link"
              className="d-inline-block"
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            {renderActivityName()}
          </Col>
        </Row>
        {
          isMobileOrTabletSize && (
            <Row>
              <Col>
                <div className="ml-auto flex-shrink-0">
                  <BotLanguageSelect
                    className="d-block ml-2"
                    size="large"
                    selectStyle={{ width: 170 }}
                  />
                </div>
              </Col>
            </Row>
          )
        }
        <Card bordered={false} className="mb-5" loading={isFetchingBot && !bot}>
          <ActivityFlowEditor activityId={searchParams.get('activity')} />
        </Card>
      </Collapse.Panel>
    </Collapse>
  );
};

export default ActivitiesView;
